/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { DatePicker, Button, Upload, Icon, Input, message } from 'antd'
import moment from 'moment'

import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import { strCol, numCol, intCol } from '@integec/grid-tools/lib/cols'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
} from 'core/utils'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'

import { DatePickerContext, SpinContext, ErrorContext } from 'context'
import {
  getEnvConfig,
  getPermitActions,
  getApiTargetInfo,
  ExportComponent,
} from 'core'
import * as ScreenPermissions from 'core/EventActions'
import { CriteriaRow, Title, FullHeightSizer, Content } from 'core/screen-util'
import {
  getCraneCreekInterfaceImport,
  getAuthorization,
  getCredentials,
} from 'api/pgads.js'
import JcTmpErrorDialog from 'core/component/JcTmpErrorDialog'

// eslint-disable-next-line no-unused-vars
const CreightoncreekImportHeaders = [
  intCol({ ident: 'year', display: 'Year', isKey: true }),
  intCol({ ident: 'month', display: 'Month', isKey: true }),
  strCol({
    ident: 'textbox4',
    display: 'Textbox4',
    isFiltered: true,
    width: 250,
  }),
  strCol({
    ident: 'textbox50',
    display: 'Textbox50',
    isFiltered: true,
    width: 300,
  }),
  strCol({ ident: 'system', display: 'System', isKey: true }),
  numCol({ ident: 'datacoverage', display: 'Datacoverage', isFiltered: true }),
  numCol({ ident: 'availability', display: 'Availability', isFiltered: true }),
  numCol({
    ident: 'availabilityadj',
    display: 'Availabilityadj',
    isFiltered: true,
  }),
  numCol({ ident: 'production', display: 'Production', isFiltered: true }),
  numCol({ ident: 'consumption', display: 'Consumption', isFiltered: true }),
  strCol({
    ident: 'gridoperatingtime',
    display: 'Gridoperatingtime',
    isFiltered: true,
  }),
  strCol({ ident: 'turbineok', display: 'Turbineok', isFiltered: true }),
  strCol({ ident: 'adjturbineok', display: 'Adjturbineok', isFiltered: true }),
  strCol({ ident: 'downtime', display: 'Downtime', isFiltered: true }),
  strCol({ ident: 'adjdowntime', display: 'Adjdowntime', isFiltered: true }),
  strCol({ ident: 'servicetime', display: 'Servicetime', isFiltered: true }),
  strCol({
    ident: 'adjservicetime',
    display: 'Adjservicetime',
    isFiltered: true,
  }),
  strCol({ ident: 'repairtime', display: 'Repairtime', isFiltered: true }),
  strCol({
    ident: 'adjrepairtime',
    display: 'Adjrepairtime',
    isFiltered: true,
  }),
  strCol({ ident: 'gridoutage', display: 'Gridoutage', isFiltered: true }),
  strCol({
    ident: 'adjgridoutage',
    display: 'Adjgridoutage',
    isFiltered: true,
  }),
  strCol({
    ident: 'weatheroutage',
    display: 'Weatheroutage',
    isFiltered: true,
  }),
  strCol({
    ident: 'adjweatheroutage',
    display: 'Adjweatheroutage',
    isFiltered: true,
  }),
  strCol({
    ident: 'externalstoptime',
    display: 'Externalstoptime',
    isFiltered: true,
  }),
  strCol({
    ident: 'externalstoppowertime',
    display: 'Externalstoppowertime',
    isFiltered: true,
  }),
  strCol({
    ident: 'customerstoptime',
    display: 'Customerstoptime',
    isFiltered: true,
  }),
  strCol({ ident: 'countersum', display: 'Countersum', isFiltered: true }),
  strCol({ ident: 'textbox51', display: 'Textbox51', isFiltered: true }),
  numCol({
    ident: 'productionenergymeter',
    display: 'Productionenergymeter',
    isFiltered: true,
  }),
  numCol({
    ident: 'consenergymeter',
    display: 'Consenergymeter',
    isFiltered: true,
  }),
  numCol({
    ident: 'prodreactiveenergy',
    display: 'Prodreactiveenergy',
    isFiltered: true,
  }),
  numCol({
    ident: 'consreactiveenergy',
    display: 'Consreactiveenergy',
    isFiltered: true,
  }),
  strCol({ ident: 'textbox11', display: 'Textbox11', isFiltered: true }),
  strCol({ ident: 'textbox105', display: 'Textbox105', isFiltered: true }),
  strCol({ ident: 'textbox14', display: 'Textbox14', isFiltered: true }),
  strCol({ ident: 'textbox106', display: 'Textbox106', isFiltered: true }),
  strCol({ ident: 'textbox16', display: 'Textbox16', isFiltered: true }),
  strCol({ ident: 'textbox113', display: 'Textbox113', isFiltered: true }),
  strCol({ ident: 'textbox17', display: 'Textbox17', isFiltered: true }),
  strCol({ ident: 'textbox120', display: 'Textbox120', isFiltered: true }),
  modifierCol,
  modifiedDateCol,
].map(h => (h.width ? h : { ...h, width: 150 }))

const CraneCreeK = ({ apiTargetInfo, envConfig, actionPermits }) => {
  const [mwImportData, setMwImportData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const [showMessages, setShowMessages] = useState(false)
  const [messages, setMessages] = useState([])
  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)

  const fetchMwImportData = () => {
    setSpinning(true)
    getCraneCreekInterfaceImport(apiTargetInfo)({
      date: moment(selectedDate).format('MM/DD/YYYY'),
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
    }).fork(setError, data => {
      setSpinning(false)
      setMwImportData(data)
    })
  }

  const fileProps = {
    name: 'file',
    action: `${apiTargetInfo.baseUrl}/interface-data/import-parser`,
    method: 'POST',
    data: {
      importType: 'CREIGHT',
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
    },
    headers: getAuthorization(apiTargetInfo),
    ...getCredentials(apiTargetInfo),
  }

  const { authGodMode } = envConfig
  const GridData = useRef()
  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <CriteriaRow>
        <Title
          css={{
            marginRight: '15px',
            marginBottom: '5px',
          }}
        >
          Crane Creek Import
        </Title>

        <DatePicker.MonthPicker
          allowClear={false}
          format="MMMM-YYYY"
          value={selectedDate}
          onChange={d => {
            setSelectedDate(d.startOf('month'))
          }}
        />

        <Button
          loading={spinning}
          onClick={fetchMwImportData}
          css={{ marginLeft: '10px' }}
          type="primary"
          icon="database"
        >
          Retrieve
        </Button>
        <div style={{ marginLeft: '50px' }} />
        <Upload
          {...fileProps}
          onChange={data => {
            if (
              data.file.response &&
              data.file.response.restfulStatus &&
              data.file.response.restfulStatus === 'FAILURE'
            ) {
              // setShowMessages(true)
              // setMessages([data.file.response])
              setError(data.file.response)
            } else if (data.file.response && data.file.response.length === 0) {
              //  if (data.file.response) {
              message.success('File imported successfully')
              // }
            } else if (data.file.response && data.file.response.length > 0) {
              setShowMessages(true)
              setMessages(data.file.response)
              //  if (data.file.response) {
              // message.success('File imported successfully')
              // }
            }
          }}
          multiple={false}
          showUploadList={false}
        >
          <Button
            disabled={
              !hasAccesPermits({
                authGodMode,
                actionPermits,
                eventActions: [ScreenPermissions.CRANECREEK_IMPORT_ADMIN],
                matchAny: true,
              })
            }
          >
            <Icon type="upload" /> Import
          </Button>
        </Upload>

        <div css={{ flex: '1 1 0px' }} />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{ marginRight: '20px', flex: '0 0 200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={
            exportDataComponent
              ? _ => exportDataComponent(GridData, mwImportData, fuzzyFilter)
              : undefined
          }
          data={mwImportData}
          headers={CreightoncreekImportHeaders}
          fileName={`Crane Creek Import-${selectedDate.format(
            'YYYY-MM-DD HH-mm-ss'
          )}`}
          btnStyle={{ marginRight: '10px' }}
          disabled={mwImportData.length <= 0}
        />
      </CriteriaRow>
      <FullHeightSizer heightOffset={230}>
        {({ height, width }) => (
          <Content style={{ height: height - 20 }}>
            <Grid
              ref={GridData}
              data={mwImportData}
              headers={CreightoncreekImportHeaders}
              altBgColor="#ebebeb"
              render={virtualizedGridRenderer({
                autoFixColByKey: false,
                rowHeaderRender,
                height: height - 50,
                width: width - 150,
                headerRowHeight: 30,
              })}
              fuzzyFilter={fuzzyFilter}
              initialSortOptions={[
                strCol({
                  ident: 'system',
                  display: 'System',
                  sortOrder: 'asc',
                }),
              ]}
            />
          </Content>
        )}
      </FullHeightSizer>
      {showMessages && (
        <JcTmpErrorDialog
          errors={messages}
          visible={showMessages}
          closeErrorPrompt={_ => setShowMessages(false)}
          title="Error/Warning"
        />
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
})

export default connect(mapStateToProps)(CraneCreeK)
